import flatPickr from 'vue-flatpickr-component';
import Select2Multiple from 'v-select2-multiple-component';
import MainLayout from "@/components/layout/admin/MainLayout.vue";
import axios from 'axios';
import store from "@/store";
import CONFIG from "@/assets/js/config.custom.js";
import tinycolor from "tinycolor2";
import {
    required,
    maxLength,
    integer,
    minValue
} from "vuelidate/lib/validators";

const MAX_ROWS = 20;

const defaultClassOption = () => [{
    id: 0,
    text: 'Select Class'
}];

const defaultPaymentOption = () => [{
    id: 0,
    text: 'Select Payment Method'
}];

const defaultAcademicYearOption = () => [{
    id: 0,
    text: 'Select Academic Year'
}];

const defaultTransactionCategoryOption = () => [{
    id: 0,
    text: 'Select Transaction Category'
}];

const createRow = () => ({
    uid: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    type: null,
    payment_date: '',
    acd_year_id: null,
    class_id: null,
    title: '',
    transaction_number: '',
    payment_method_id: null,
    amount: '',
    incharge_name: '',
    remark: '',
    transaction_category_id: null,
    payment_screenshot: '',
    payment_screenshot_name: '',
});

export default {
    name: 'TransactionBatchCreate',
    components: {
        MainLayout,
        flatPickr,
        Select2Multiple,
    },

    data() {
        return {
            imageLargeMsg: CONFIG.IMAGE_TOO_LARGE,
            maxRows: MAX_ROWS,
            disabled: false,
            submitted: false,
            rows: [createRow()],
            academicYearArr: defaultAcademicYearOption(),
            paymentMethodArr: defaultPaymentOption(),
            transactionCategoryArrByType: {},
            typeOptions: CONFIG.TRANSACTION_TYPES_OPTIONS,
            classArr: {},
            rowErrors: {},
        };
    },

    validations: {
        rows: {
            $each: {
                type: {
                    required
                },
                acd_year_id: {
                    required,
                    integer,
                },
                class_id: {
                    integer,
                },
                title: {
                    required,
                    maxLength: maxLength(100)
                },
                transaction_number: {
                    maxLength: maxLength(100)
                },
                payment_method_id: {
                    required,
                },
                payment_date: {
                    required
                },
                amount: {
                    required,
                    integer,
                    maxLength: maxLength(9),
                    minValue: minValue(1)
                },
                incharge_name: {
                    required,
                    maxLength: maxLength(100)
                },
                remark: {
                    maxLength: maxLength(1000)
                }
            }
        }
    },

    computed: {
        /**
         * Build Select2 settings for transaction category options.
         *
         * @returns {Object}
         */
        transactionCategorySelectSettings() {
            return {
                multiple: false,
                placeholder: 'Select Transaction Category',
                templateResult: (option) => this.formatTransactionCategoryOption(option),
                templateSelection: (option) => this.formatTransactionCategoryOption(option),
                escapeMarkup: (markup) => markup,
            };
        },
    },

    methods: {
        /**
         * Normalize a backend validation message to a single string.
         *
         * @param {string|string[]} message
         * @returns {string|undefined}
         */
        normalizeRowError(message) {
            return Array.isArray(message) ? message[0] : message;
        },

        /**
         * Prefix a list of options with a default placeholder option.
         *
         * @param {Function} defaultOptionFactory
         * @param {Array} [options=[]]
         * @returns {Array}
         */
        withDefaultOption(defaultOptionFactory, options = []) {
            return defaultOptionFactory().concat(options);
        },

        /**
         * Escape raw text before rendering it in HTML markup.
         *
         * @param {string} text
         * @returns {string}
         */
        escapeHtml(text) {
            return String(text ?? "")
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/"/g, "&quot;")
                .replace(/'/g, "&#39;");
        },

        /**
         * Validate and normalize a hex color value.
         *
         * @param {string} colorCode
         * @returns {string}
         */
        validHexColor(colorCode) {
            const input = String(colorCode ?? "").trim();
            const standardHexRegex = /^#([0-9A-Fa-f]{3}){1,2}$/;

            if (!standardHexRegex.test(input)) {
                return "";
            }

            const color = tinycolor(input);

            return color.isValid() ? color.toHexString() : "";
        },

        /**
         * Render a transaction category option with its optional color swatch.
         *
         * @param {Object} option
         * @returns {string}
         */
        formatTransactionCategoryOption(option) {
            if (!option || Number(option.id) === 0) {
                return this.escapeHtml(option?.text ?? "Select Transaction Category");
            }

            const safeText = this.escapeHtml(option.text);
            const safeColor = this.validHexColor(option.color_code);

            if (safeColor) {
                return `<span style="display:flex;align-items:center;gap:8px;">
                            <span style="width:15px;height:15px;background:${safeColor};border:1px solid #d1d5db;display:inline-block;border-radius:2px;"></span>
                            <span>${safeText}</span>
                        </span>`;
            }

            return `<span>${safeText}</span>`;
        },

        /**
         * Get available class options for the selected academic year.
         *
         * @param {Object} row
         * @returns {Array}
         */
        getClassOptions(row) {
            if (!row.acd_year_id || Number(row.acd_year_id) === 0) {
                return defaultClassOption();
            }

            return this.classArr[row.acd_year_id] || defaultClassOption();
        },

        /**
         * Get cached transaction category options for the row type.
         *
         * @param {Object} row
         * @returns {Array}
         */
        getTransactionCategoryOptions(row) {
            const type = String(row?.type ?? '').trim();
            if (!type) {
                return defaultTransactionCategoryOption();
            }

            return this.transactionCategoryArrByType[type] || defaultTransactionCategoryOption();
        },

        /**
         * Load transaction category options for the given type.
         *
         * @param {string|number|null} type
         * @returns {Promise<Array>}
         */
        getTransactionCategory(type) {
            if (!type) {
                return Promise.resolve(defaultTransactionCategoryOption());
            }

            const normalizedType = String(type).trim();

            if (this.transactionCategoryArrByType[normalizedType]) {
                return Promise.resolve(this.transactionCategoryArrByType[normalizedType]);
            }

            return axios
                .get(`/transactionCategory/getTransactionCategoryArray?type=${normalizedType}`)
                .then((res) => {
                    const categoryList = Array.isArray(res.data?.[0]) ? res.data[0] : [];
                    const categoryOptions = categoryList.map((category) => ({
                        ...category,
                        text: category?.text ?? '',
                        color_code: category?.color_code ?? null,
                    }));
                    const options = this.withDefaultOption(
                        defaultTransactionCategoryOption,
                        categoryOptions
                    );
                    this.$set(this.transactionCategoryArrByType, normalizedType, options);

                    return options;
                })
                .catch((error) => {
                    if (error.response && error.response.status == 401) {
                        this.$store.commit('logout');
                        window.location.replace('login');
                    }

                    throw error;
                });
        },

        /**
         * Reset and reload category data after a row type change.
         *
         * @param {Object} row
         * @returns {void}
         */
        handleTypeChange(row) {
            row.transaction_category_id = null;
            this.clearRowFieldError(row.uid, 'type');
            this.clearRowFieldError(row.uid, 'transaction_category_id');

            if (!row.type) {
                return;
            }

            this.getTransactionCategory(row.type)
                .catch(() => {
                    this.$notification.error("Unable to load transaction categories.", {
                        timer: 3,
                        position: "bottomCenter",
                    });
                });
        },

        /**
         * Preload transaction categories for all supported transaction types.
         *
         * @returns {void}
         */
        preloadTransactionCategories() {
            this.typeOptions.forEach((option) => {
                this.getTransactionCategory(option.id).catch(() => {});
            });
        },

        /**
         * Load class options for the selected academic year in a row.
         *
         * @param {Object} row
         * @returns {void}
         */
        getClass(row) {
            row.class_id = null;
            this.clearRowFieldError(row.uid, 'acd_year_id');
            this.clearRowFieldError(row.uid, 'class_id');

            if (!row.acd_year_id || Number(row.acd_year_id) === 0) {
                return;
            }

            if (this.classArr[row.acd_year_id]) {
                return;
            }

            axios.get("class/classList/" + row.acd_year_id, {
                params: {
                    campus_id: this.$store.state.user.campus_id,
                }
            })
                .then(res => {
                    this.$set(
                        this.classArr,
                        row.acd_year_id,
                        this.withDefaultOption(defaultClassOption, res.data)
                    );
                }).catch((error) => {
                    if (error.response && error.response.status == 401) {
                        this.$store.commit('logout');
                        window.location.replace('login');
                    }
                    this.$notification.error("Unable to load class list.", {
                        timer: 3,
                        position: "bottomCenter",
                    });
                });
        },

        /**
         * Load available payment methods.
         *
         * @returns {void}
         */
        getPaymentMethod() {
            axios.get('paymentMethod/getPaymentMethodArray')
                .then(res => {
                    this.paymentMethodArr = this.withDefaultOption(
                        defaultPaymentOption,
                        res.data[0]
                    );
                }).catch((error) => {
                    if (error.response && error.response.status == 401) {
                        this.$store.commit('logout');
                        window.location.replace('login');
                    }
                });
        },

        /**
         * Load academic year options for the current campus.
         *
         * @returns {void}
         */
        getAcademicYear() {
            axios.get('getAcademicYear', {
                params: {
                    campus_id: store.state.user.campus_id
                }
            })
                .then(res => {
                    this.academicYearArr = this.withDefaultOption(
                        defaultAcademicYearOption,
                        res.data.academicYearArr
                    );
                }).catch((error) => {
                    if (error.response && error.response.status == 401) {
                        this.$store.commit('logout');
                        window.location.replace('login');
                    }
                });
        },

        /**
         * Get the file input element for a row screenshot field.
         *
         * @param {string} rowUid
         * @returns {HTMLInputElement|null}
         */
        getPaymentScreenshotInput(rowUid) {
            const fileInput = this.$refs[`paymentScreenshot${rowUid}`];
            return Array.isArray(fileInput) ? fileInput[0] : fileInput;
        },

        /**
         * Clear the file input element for a row screenshot field.
         *
         * @param {string} rowUid
         * @returns {void}
         */
        resetPaymentScreenshotInput(rowUid) {
            const fileInput = this.getPaymentScreenshotInput(rowUid);
            if (fileInput) {
                fileInput.value = '';
            }
        },

        /**
         * Remove the selected payment screenshot from a row.
         *
         * @param {Object} row
         * @returns {void}
         */
        removePaymentScreenshot(row) {
            row.payment_screenshot = '';
            row.payment_screenshot_name = '';
            this.resetPaymentScreenshotInput(row.uid);
        },

        /**
         * Validate and encode an uploaded payment screenshot.
         *
         * @param {Object} row
         * @param {Event} e
         * @returns {null}
         */
        uploadPaymentScreenshot(row, e) {
            let files = e.target.files || e.dataTransfer.files;
            if (!files || !files.length) {
                return null;
            }

            const selectedFile = files[0];
            if (!selectedFile.type.includes("image/") || selectedFile.type == "image/gif") {
                this.$notification.error("File not supported!", {
                    timer: 3,
                    position: "bottomCenter",
                });
                this.resetPaymentScreenshotInput(row.uid);
                return null;
            }

            if (selectedFile.size > 5242880) {
                this.$notification.error(this.imageLargeMsg, {
                    timer: 3,
                    position: "bottomCenter",
                });
                this.resetPaymentScreenshotInput(row.uid);
                return null;
            }

            let reader = new FileReader();
            reader.onload = (event) => {
                row.payment_screenshot = event.target.result;
                row.payment_screenshot_name = selectedFile.name;
            };
            reader.readAsDataURL(selectedFile);
            return null;
        },

        /**
         * Append a new blank transaction row if the row limit is not reached.
         *
         * @returns {void}
         */
        addRow() {
            if (this.rows.length >= this.maxRows) {
                this.$notification.error(`You can add up to ${this.maxRows} rows only.`, {
                    timer: 3,
                    position: "bottomCenter",
                });
                return;
            }

            const newRow = createRow();
            this.rows.push(newRow);

            if (newRow.acd_year_id && Number(newRow.acd_year_id) !== 0) {
                this.getClass(newRow);
            }
        },

        /**
         * Remove a transaction row.
         *
         * @param {number} index
         * @returns {void}
         */
        removeRow(index) {
            const scrollContainer = this.$el.querySelector('.table-scroll');
            const currentScrollLeft = scrollContainer ? scrollContainer.scrollLeft : 0;

            const removedRow = this.rows[index];
            this.rows.splice(index, 1);
            if (removedRow) {
                this.clearRowErrors(removedRow.uid);
            }

            if (this.rows.length === 1) {
                this.$nextTick(() => {
                    scrollContainer.scrollLeft = currentScrollLeft;
                });
            }
        },

        /**
         * Clear validation errors for one row or for all rows.
         *
         * @param {string|null} [rowUid=null]
         * @returns {void}
         */
        clearRowErrors(rowUid = null) {
            if (rowUid) {
                this.$delete(this.rowErrors, rowUid);
                return;
            }

            this.rowErrors = {};
        },

        /**
         * Clear a specific validation error for a row.
         *
         * @param {string} rowUid
         * @param {string} fieldName
         * @returns {void}
         */
        clearRowFieldError(rowUid, fieldName) {
            if (!rowUid || !fieldName || !this.rowErrors[rowUid]) {
                return;
            }

            this.$delete(this.rowErrors[rowUid], fieldName);

            if (!Object.keys(this.rowErrors[rowUid]).length) {
                this.$delete(this.rowErrors, rowUid);
            }
        },

        /**
         * Clear field-level validation errors after a row value changes.
         *
         * @param {Object} row
         * @param {string|null} [fieldName=null]
         * @returns {void}
         */
        handleRowChange(row, fieldName = null) {
            if (fieldName) {
                this.clearRowFieldError(row.uid, fieldName);
            }

            const scrollContainer = this.$el.querySelector('.table-scroll');
            if (scrollContainer) {
                const currentScrollLeft = scrollContainer.scrollLeft;
                this.$nextTick(() => {
                    scrollContainer.scrollLeft = currentScrollLeft;
                });
            }
        },

        /**
         * Check whether any backend row validation errors are present.
         *
         * @returns {boolean}
         */
        hasRowErrors() {
            return Object.keys(this.rowErrors).length > 0;
        },

        /**
         * Map backend validation errors to the matching batch row state.
         *
         * @param {Object} [errors={}]
         * @returns {void}
         */
        applyBackendValidationErrors(errors = {}) {
            Object.entries(errors).forEach(([fieldKey, messages]) => {
                const match = fieldKey.match(/^transactions\.(\d+)\.(.+)$/);
                if (!match) {
                    return;
                }

                const rowIndex = Number(match[1]);
                const fieldName = match[2];
                const row = this.rows[rowIndex];

                if (!row) {
                    return;
                }

                if (!this.rowErrors[row.uid]) {
                    this.$set(this.rowErrors, row.uid, {});
                }

                this.$set(
                    this.rowErrors[row.uid],
                    fieldName,
                    this.normalizeRowError(messages)
                );
            });
        },

        /**
         * Validate the form and submit the batch transaction payload.
         *
         * @returns {void}
         */
        create() {
            if (this.disabled) return;

            this.submitted = true;
            this.clearRowErrors();
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            }

            this.disabled = true;

            const payload = {
                loginID: this.$store.state.user.id,
                campus_id: this.$store.state.user.campus_id,
                transactions: this.rows.map((row) => ({
                    type: row.type,
                    title: `${row.title ?? ''}`.trim(),
                    transaction_number: `${row.transaction_number ?? ''}`.trim(),
                    payment_method_id: row.payment_method_id,
                    payment_date: row.payment_date,
                    class_id: Number(row.class_id) || null,
                    acd_year_id: row.acd_year_id,
                    amount: `${row.amount}`.trim(),
                    payment_screenshot: row.payment_screenshot || null,
                    incharge_name: `${row.incharge_name ?? ''}`.trim(),
                    remark: `${row.remark ?? ''}`.trim(),
                    transaction_category_id: Number(row.transaction_category_id) || null,
                })),
            };

            axios.post('transaction/batch-create', payload)
                .then((res) => {
                    if (res.data?.success) {
                        this.$notification.success("Transactions Added Successfully", {
                            timer: 3,
                            position: "bottomCenter",
                        });
                        this.$router.push({ name: 'transactionList' });
                    }
                })
                .catch((error) => {
                    this.disabled = false;

                    if (error.response?.status === 422) {
                        this.clearRowErrors();
                        this.applyBackendValidationErrors(error.response.data?.errors || {});
                        if (!this.hasRowErrors()) {
                            this.$notification.error("Please fix the validation issues first.", {
                                timer: 3,
                                position: "bottomCenter",
                            });
                        }
                        return;
                    }

                    if (error.response?.status == 401) {
                        this.$store.commit('logout');
                        window.location.replace('login');
                        return;
                    }

                    this.$notification.error("Unable to create transactions.", {
                        timer: 3,
                        position: "bottomCenter",
                    });
                });
        },
    },

    mounted() {
        this.getPaymentMethod();
        this.getAcademicYear();
        this.preloadTransactionCategories();

        const firstRow = this.rows[0];
        if (firstRow.acd_year_id && Number(firstRow.acd_year_id) !== 0) {
            this.getClass(firstRow);
        }
    }
};
